import EventEmitter from "events";
import TypedEmitter from "typed-emitter";
import { ClientEventsType } from "~/modules/client/Client/ClientEvent";
import { Workspace, Supervisor } from "~/packages/taskrouter";
import { Client, ClientEvent } from "~/modules/client";
import { Session, SessionEvent } from "~/modules/session";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import type { TaskRouter } from "~/packages/taskrouter/TaskRouter";
import { assertNotEmptyString } from "~/utils/assert";
import { ErrorCode, ErrorSeverity, FlexSdkErrorMetadata } from "~/modules/error";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { AnalyticsInstance, EVENTS } from "~/modules/telemetry/Analytics";
import { sendLifecycleTrackingEvent } from "~/modules/telemetry/utils";
import { Action } from "~/modules/actions/Action";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

export class ClientImpl
    extends (EventEmitter as unknown as new () => TypedEmitter<ClientEventsType>)
    implements Client
{
    readonly #session: Session;

    readonly #logger: Logger;

    readonly #ctx: ContextManager;

    readonly #analytics: AnalyticsInstance;

    readonly #taskRouter: TaskRouter;

    #isDestroyed: boolean;

    constructor(context?: ContextManager) {
        super();
        if (context) {
            this.#ctx = context;
        } else {
            this.#ctx = ContextManager.newInstance();
        }
        this.#session = this.#ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#logger = getLogger(this.#ctx)(LoggerName.Client);
        this.#analytics = this.#ctx.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        this.#taskRouter = this.#ctx.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        this.setupProxies();
    }

    setupProxies(): void {
        this.#session.addListener(SessionEvent.TokenAutoUpdateFailed, () => {
            this.emit(ClientEvent.TokenAutoUpdateFailed);
        });
        this.#session.addListener(SessionEvent.TokenMaxLifetimeReached, (newTokenDateExpired) => {
            this.emit(ClientEvent.TokenMaxLifetimeReached, newTokenDateExpired);
        });
    }

    updateToken(token: string, refreshToken?: string): void {
        assertNotEmptyString(token, "token");
        if (this.#isDestroyed) {
            throwFlexSdkError(this.#ctx)(
                ErrorCode.InvalidState,
                { severity: ErrorSeverity.Error },
                "Client.updateToken: Client already destroyed"
            );
        }
        this.emit(ClientEvent.TokenUpdated, token);

        if (this.#session) {
            try {
                this.#session.updateToken(token, refreshToken);
            } catch (error) {
                const errorMsg = "Client.updateToken: Failed to update token on session";
                this.#logger.error(errorMsg, error);
                throwFlexSdkError(this.#ctx)(
                    ErrorCode.FailedToUpdateTokenOnSession,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                );
            }
        } else {
            this.#logger.warn("Client.updateToken: Session is undefined, skipping token update for session");
        }
    }

    readonly #sendDestroyEvent = (): void => {
        try {
            sendLifecycleTrackingEvent(EVENTS.ClientDestroyed, this.#analytics, this.#session.accountSid);
        } catch (error) {
            this.#logger.error("Client.sendDestroyEvent: Failed to send telemetry destroy event", error);
        }
    };

    #throwUnexpectedError(error: Error): void {
        const metadata: FlexSdkErrorMetadata = {
            module: "client",
            severity: ErrorSeverity.Error,
            source: "ClientImpl.ts"
        };

        throwFlexSdkError(this.#ctx)(
            ErrorCode.UnexpectedError,
            metadata,
            "Client.execute: Unexpected error occurred",
            error
        );
    }

    destroy(): void {
        this.#logger.debug("Client destroy called");
        if (this.#isDestroyed) {
            return;
        }

        this.#isDestroyed = true;
        this.#sendDestroyEvent();
        this.#session.destroy();
        this.emit(ClientEvent.ClientDestroyed);
        this.removeAllListeners();
        this.#ctx.destroy();
        this.#logger.debug("Client has been destroyed");
    }

    get roles(): Array<string> {
        sendLifecycleTrackingEvent(EVENTS.ClientRolesCompleted, this.#analytics, this.#session.accountSid);
        return [...this.#session.roles];
    }

    get token(): string {
        sendLifecycleTrackingEvent(EVENTS.ClientTokenCompleted, this.#analytics, this.#session.accountSid);
        return this.#session.token;
    }

    async getWorker(): Promise<Supervisor> {
        await this.#taskRouter.init();
        return this.#taskRouter.worker;
    }

    async getWorkspace(): Promise<Workspace> {
        await this.#taskRouter.init();
        return this.#taskRouter.workspace;
    }

    execute<T>(action: Action<T>): T {
        try {
            const result = action.run(this.#ctx);

            
            if (result instanceof Promise) {
                
                
                return result.catch((error: Error) => this.#throwUnexpectedError(error)) as T;
            }
            return result;
        } catch (error) {
            return this.#throwUnexpectedError(error instanceof Error ? error : new Error(String(error))) as T;
        }
    }
}
